import { Moment } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { isEmpty } from 'lodash'
import React, { forwardRef, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import NewBadge from '#/components/badges/NewBadge'
import { Icon } from '#/components/Icon'
import { useUserInfo } from '#/store/ducks/auth'
import { useFarmSlug } from '#/store/ducks/farm'
import { usePermissions } from '#/store/ducks/permissions'
import Palette from '#/styles/Old/Palette'
import isBusinessEnvironment from '#/utils/isBusinessEnvironment'
import { routerMatch } from '#/utils/routerMatch'

const StyledList = styled(List)({
  paddingBottom: 0,
})

const StyledSubheader = styled(ListSubheader)({
  lineHeight: '28px',
  fontSize: '0.9em',
})

const StyledListItemText = styled(ListItemText)({
  color: Palette.white.self,
})

const LinkListItem = styled(ListItem)(({ theme, selected }) => ({
  ...(selected
    ? {
        boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        backgroundColor: 'rgba(0,0,0,.1)',
      }
    : {
        color: theme.palette.grey['400'],
      }),
  '&:hover': {
    '& svg': { color: theme.palette.primary.main },
    '& span': { color: theme.palette.primary.main },
    'backgroundColor': Palette.transparent[2],
  },
  'alignItems': 'center',
}))

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  minWidth: 35,
  marginTop: 4,
  marginBottom: 4,
}))

const Menu = () => {
  const { has_company } = useUserInfo()

  const [open, setOpen] = useState()
  const farmSlug = useFarmSlug()

  const permissions = usePermissions()

  function checkSelected(item) {
    let selected = false
    if (item.link) {
      selected = routerMatch(item)
    } else {
      selected = !isEmpty(item.items?.filter(subItem => subItem.link && routerMatch(subItem)))
    }

    return selected
  }

  let modules = {
    b2b: [
      //Home
      {
        enabled: true,
        key: 'access',
        subheader: 'Dashboards',
        link: '/',
        icon: <Icon name="home" size={21} />,
        label: Translate({ messageKey: 'accesses' }),
      },
      //Data View
      {
        enabled: permissions.system.view_data_view,
        key: 'data-view',
        subheader: 'Data Analytics',
        link: `/data-view`,
        icon: <Icon name="data-analytics" size={21} />,
        label: Translate({ messageKey: 'data_view' }),
      },
      //Farm Database
      {
        key: 'database',
        subheader: 'Farm',
        icon: <Icon name="farm-database" size={21} />,
        label: Translate({ messageKey: 'farm_database' }),
        collapsable: true,
        collapseKey: 'database',
        items: [
          {
            enabled: permissions.system.list_semen,
            key: 'semen',
            link: '/database/semen',
            label: Translate({ messageKey: 'bull_or_semen' }),
          },
          {
            enabled: permissions.system.list_treatment,
            key: 'treatment',
            link: '/database/treatment',
            label: Translate({ messageKey: 'treatment_plural' }),
          },
        ],
      },
      //Contas
      {
        key: 'accounts',
        subheader: 'System',
        icon: <Icon name="account" size={21} />,
        label: Translate({ messageKey: 'account_plural' }),
        collapsable: true,
        collapseKey: 'accounts',
        items: [
          {
            enabled: permissions.system.list_account_farm,
            key: 'farms',
            link: '/accounts/farm',
            label: Translate({ messageKey: 'farm_plural' }),
          },
          {
            enabled: has_company && permissions.system.list_company,
            key: 'list',
            link: '/accounts/company',
            label: Translate({ messageKey: 'company_plural' }),
          },
          {
            enabled: permissions.system.list_account_farm,
            key: 'renewals',
            link: '/accounts/renewals',
            label: Translate({ messageKey: 'renewals' }),
          },
        ],
      },
      //CS
      {
        key: 'cs',
        icon: <Icon size="large" name="face-agent" />,
        label: Translate({ messageKey: 'cs' }),
        collapsable: true,
        collapseKey: 'cs',
        items: [
          {
            enabled: permissions.system.list_account_farm_cs,
            key: 'customer-center',
            link: '/cs/customer-center',
            label: Translate({ messageKey: 'customer_center' }),
          },
          {
            enabled: permissions.system.list_account_farm_integrations,
            key: 'integrations',
            link: '/cs/integrations/api',
            label: Translate({ messageKey: 'integrations' }),
          },
          {
            enabled: permissions.system.list_account_farm_nps,
            key: 'nps',
            link: '/cs/nps/campaigns',
            label: 'NPS',
          },
          {
            enabled: permissions.system.list_semen,
            key: 'bull-migration',
            link: '/cs/bull-migration',
            label: Translate({ messageKey: 'bull_migration' }),
          },
        ],
      },
      //C-tech
      {
        key: 'c-tech',
        icon: <Icon name="c-tech" size={21} />,
        label: Translate({ messageKey: 'c_tech' }),
        collapsable: true,
        collapseKey: 'c-tech',
        items: [
          {
            enabled: permissions.system.list_collar_operational_status,
            key: 'collar-status',
            link: '/system/inventory/c-tech/status',
            label: Translate({ messageKey: 'operational_status' }),
          },
          {
            enabled: permissions.system.list_collar,
            key: 'collar',
            exact: true,
            link: '/system/inventory/c-tech/list',
            label: Translate({ messageKey: 'device_list' }),
          },
          {
            enabled: permissions.system.list_collar,
            key: 'collar-break',
            link: '/system/inventory/c-tech/break',
            label: Translate({ messageKey: 'break' }),
          },
        ],
      },
      //C-env
      {
        key: 'c-env',
        icon: <Icon name="cow-comfort" size={21} />,
        label: Translate({ messageKey: 'cow_comfort' }),
        collapsable: true,
        collapseKey: 'c-env',
        items: [
          {
            enabled: permissions.system.list_cenv,
            key: 'cenv-status',
            exact: true,
            link: '/system/inventory/c-env',
            label: Translate({ messageKey: 'device_list' }),
          },
        ],
      },
      //C-com
      {
        key: 'c-com',
        icon: <Icon size="large" name="access-point-network" />,
        label: Translate({ messageKey: 'c_com' }),
        collapsable: true,
        collapseKey: 'c-com',
        items: [
          {
            enabled: permissions.system.list_ccom,
            key: 'ccom-status',
            link: '/system/inventory/c-com/status',
            label: Translate({ messageKey: 'operational_status' }),
          },
          {
            enabled: permissions.system.list_ccom,
            key: 'ccom',
            exact: true,
            link: '/system/inventory/c-com',
            label: Translate({ messageKey: 'device_list' }),
          },
          {
            enabled:
              permissions.system.view_farm_data_delays || permissions.system.view_batch_data_delays,
            key: 'ccom-interval',
            link: '/system/inventory/c-com/interval',
            label: Translate({ messageKey: 'data_interval' }),
          },
        ],
      },
      {
        key: 'access_management',
        icon: <Icon name="shield-user" size={20} />,
        label: Translate({ messageKey: 'access_management' }),
        collapsable: true,
        collapseKey: 'access_management',
        items: [
          {
            enabled: permissions.system.update_permission,
            key: 'permissions',
            link: '/system/access-management/permissions',
            label: Translate({ messageKey: 'permission_plural' }),
          },
          {
            enabled: permissions.system.list_role,
            key: 'roles',
            link: '/system/access-management/roles',
            label: Translate({ messageKey: 'role_plural' }),
          },
          {
            enabled: permissions.system.list_module,
            key: 'modules',
            link: '/system/access-management/modules',
            label: Translate({ messageKey: 'module_plural' }),
          },
          {
            enabled: permissions.system.manage_system_user,
            key: 'system-users',
            link: '/system/access-management/system-users/users',
            label: Translate({ messageKey: 'system_users' }),
          },
        ],
      },
      //Root
      {
        key: 'root',
        icon: <Icon name="user-lock" size={20} />,
        label: Translate({ messageKey: 'root' }),
        collapsable: true,
        collapseKey: 'root',
        items: [
          {
            enabled: permissions.system.list_release_notes,
            key: 'release-notes',
            link: '/system/settings/release-notes',
            label: Translate({ messageKey: 'release_notes' }),
          },
          {
            enabled: permissions.system.create_help || permissions.system.update_help,
            key: 'help-pages',
            link: '/system/settings/help-pages',
            label: Translate({ messageKey: 'help_center' }),
          },
          {
            enabled: permissions.system.list_mail_recipient,
            key: 'mails',
            link: '/system/settings/mails',
            label: Translate({ messageKey: 'transactional_emails' }),
          },
          {
            enabled: permissions.system.access_telescope,
            key: 'tests',
            link: '/system/settings/tests',
            label: Translate({ messageKey: 'test_plural' }),
          },
        ],
      },
      //AI
      {
        key: 'AI',
        icon: <Icon name="brain-circuit" size={20} />,
        label: Translate({ messageKey: 'ai' }),
        collapsable: true,
        collapseKey: 'AI',
        items: [
          {
            enabled: permissions.system.view_rag,
            key: 'rags',
            link: '/system/ai/rags',
            label: Translate({ messageKey: 'rags' }),
          },
          {
            enabled:
              permissions.system.view_token_consumption ||
              permissions.system.manage_token_consumption,
            key: 'tokens',
            link: '/system/ai/tokens',
            label: Translate({ messageKey: 'token_consumption' }),
          },
        ],
      },
      //Import
      {
        enabled: permissions.system.list_system_job,
        key: 'system-import',
        link: `/system/import`,
        icon: <Icon name="cloud-upload" size={20} />,
        label: Translate({ messageKey: 'import_data' }),
      },
    ],
    b2c: [
      //Home
      {
        enabled: true,
        exact: true,
        key: 'home-dashboard',
        subheader: 'Menu',
        link: `/farm/${farmSlug}`,
        icon: <Icon name="home" size={21} />,
        label: Translate({ messageKey: 'home' }),
      },
      //Rebanho
      {
        key: 'batches-cattle',
        icon: <Icon name="animal" size={21} />,
        label: Translate({ messageKey: 'herd' }),
        collapsable: true,
        collapseKey: 'herd',
        items: [
          {
            enabled: permissions.farm.list_animal,
            key: 'animal-management',
            link: `/farm/${farmSlug}/modules/herd/animals`,
            label: Translate({ messageKey: 'animal_plural' }),
          },
          {
            enabled: permissions.farm.list_batch,
            key: 'batch-management',
            link: `/farm/${farmSlug}/modules/herd/batch`,
            label: Translate({ messageKey: 'batch_plural' }),
          },
          {
            enabled: permissions.farm.relate_animal_batch,
            key: 'batch-handling',
            link: `/farm/${farmSlug}/modules/herd/manage`,
            label: Translate({ messageKey: 'change_group_plural' }),
          },
          {
            enabled: permissions.farm.view_batch_ccom_divergence,
            key: 'batch-divergence',
            link: `/farm/${farmSlug}/modules/herd/divergence`,
            label: Translate({ messageKey: 'batch_divergence' }),
          },
        ],
      },
      //Cria/Recria
      {
        key: 'rearing',
        icon: <Icon name="rearing" size={21} />,
        label: Translate({ messageKey: 'rearing_raising' }),
        collapsable: true,
        collapseKey: 'rearing',
        items: [
          {
            enabled: permissions.farm.list_heifer_health_event,
            key: 'rearing-health-management',
            link: `/farm/${farmSlug}/modules/health/infirmary-baby`,
            label: Translate({ messageKey: 'raising_infirmary' }),
          },
          {
            enabled: permissions.farm.list_animal,
            key: 'rearing-weaning-management',
            link: `/farm/${farmSlug}/modules/rearing/weaning`,
            label: Translate({ messageKey: 'weaning' }),
          },
        ],
      },
      //Reproducao
      {
        key: 'reproduction',
        icon: <Icon name="reproduction" size={21} />,
        label: Translate({ messageKey: 'reproduction' }),
        collapsable: true,
        collapseKey: 'reproduction',
        items: [
          {
            enabled:
              permissions.farm.list_reproduction_event && permissions.farm.list_reproduction_alert,
            key: 'reproduction-alerts',
            link: `/farm/${farmSlug}/modules/reproduction/heat`,
            label: Translate({ messageKey: 'heat_alert_plural' }),
          },
          {
            enabled: permissions.farm.list_animal,
            key: 'reproduction-reproductive-evaluation',
            link: `/farm/${farmSlug}/modules/reproduction/reproductive-evaluation`,
            label: Translate({ messageKey: 'reproductive_evaluation' }),
          },
          {
            enabled: permissions.farm.list_animal,
            key: 'reproduction-gynecologic-examination',
            link: `/farm/${farmSlug}/modules/reproduction/gynecologic`,
            label: Translate({ messageKey: 'gynecologic_examination' }),
          },
          {
            enabled: permissions.farm.list_animal && permissions.farm.list_reproduction_event,
            key: 'reproduction-pregnancy-diagnosis',
            link: `/farm/${farmSlug}/modules/reproduction/pregnancy`,
            label: Translate({ messageKey: 'pregnancy_diagnosis' }),
          },
          {
            enabled: permissions.farm.list_protocol_schedule,
            key: 'reproduction-protocol-schedule',
            link: `/farm/${farmSlug}/modules/reproduction/protocols`,
            label: Translate({ messageKey: 'protocol_plural' }),
          },
          {
            enabled: permissions.farm.list_reproduction_event,
            key: 'reproduction-drying',
            link: `/farm/${farmSlug}/modules/reproduction/drying`,
            label: Translate({ messageKey: 'drying' }),
          },
          {
            enabled: permissions.farm.list_animal && permissions.farm.list_reproduction_event,
            key: 'reproduction-pre-calving-prediction',
            link: `/farm/${farmSlug}/modules/reproduction/pre-calving-prediction`,
            label: Translate({ messageKey: 'pre_calving' }),
          },
          {
            enabled: permissions.farm.list_reproduction_event,
            key: 'reproduction-calving',
            link: `/farm/${farmSlug}/modules/reproduction/calving/first-calving`,
            label: Translate({ messageKey: 'calving_plural' }),
          },
          {
            enabled: permissions.farm.list_farm_semen,
            key: 'farm-bull-semen',
            link: `/farm/${farmSlug}/modules/reproduction/bull-semen`,
            label: Translate({ messageKey: 'bull_or_semen' }),
          },
          {
            enabled: permissions.farm.list_matrix,
            key: 'farm-matrix',
            link: `/farm/${farmSlug}/modules/reproduction/matrix`,
            label: Translate({ messageKey: 'matrix_plural' }),
          },
          {
            enabled: permissions.farm.list_embryo_donor,
            key: 'farm-embryo-donor',
            link: `/farm/${farmSlug}/modules/reproduction/embryo-donor`,
            label: Translate({ messageKey: 'donor_plural' }),
          },
          {
            enabled: permissions.farm.list_farm_inseminator,
            key: 'farm-inseminator',
            link: `/farm/${farmSlug}/modules/reproduction/inseminator`,
            label: Translate({ messageKey: 'inseminator' }),
          },
          {
            enabled: permissions.farm.list_mating,
            key: 'farm-mating',
            link: `/farm/${farmSlug}/modules/reproduction/mating`,
            label: Translate({ messageKey: 'mating' }),
          },
        ],
      },
      //Saude
      {
        key: 'health',
        icon: <Icon name="health" size={21} />,
        label: Translate({ messageKey: 'health' }),
        collapsable: true,
        collapseKey: 'health',
        items: [
          {
            enabled: permissions.farm.list_sanitary_protocol,
            key: 'health-applications',
            link: `/farm/${farmSlug}/modules/health/applications`,
            label: Translate({ messageKey: 'applications' }),
          },
          {
            enabled: permissions.farm.list_health_event && permissions.farm.list_health_alert,
            key: 'health-management',
            link: `/farm/${farmSlug}/modules/health/infirmary`,
            label: Translate({ messageKey: 'infirmary' }),
          },
          {
            enabled: permissions.farm.list_heifer_health_event,
            key: 'health-baby-management',
            link: `/farm/${farmSlug}/modules/health/infirmary-baby`,
            label: Translate({ messageKey: 'raising_infirmary' }),
          },
          {
            enabled: permissions.farm.list_health_event && permissions.farm.list_health_alert,
            key: 'health-challenge',
            link: `/farm/${farmSlug}/modules/health/challenge`,
            label: Translate({ messageKey: 'challenged_animals' }),
          },
          {
            enabled: permissions.farm.list_sanitary_protocol,
            key: 'health-protocols',
            link: `/farm/${farmSlug}/modules/health/protocols/my-protocols`,
            label: Translate({ messageKey: 'health_protocols' }),
          },
          {
            enabled: permissions.farm.list_health_event,
            key: 'health-withdrawal',
            link: `/farm/${farmSlug}/modules/health/withdrawal`,
            label: Translate({ messageKey: 'animals_in_withdrawal' }),
          },
        ],
      },
      //Nutricao
      {
        key: 'nutrition',
        icon: <Icon name="nutrition" size={21} />,
        label: Translate({ messageKey: 'nutrition' }),
        collapsable: true,
        collapseKey: 'nutrition',
        items: [
          {
            enabled: permissions.farm.list_batch && permissions.farm.list_milk_production,
            key: 'nutrition-management',
            link: `/farm/${farmSlug}/modules/precision-nutrition/monitored`,
            label: Translate({
              messageKey: 'item_monitored_plural',
              params: {
                item: Translate({ messageKey: 'batch_plural', lower: true }),
              },
            }),
          },
          {
            enabled: permissions.farm.list_ingredient,
            key: 'nutrition-management-ingredient',
            link: `/farm/${farmSlug}/modules/precision-nutrition/ingredient`,
            label: Translate({ messageKey: 'ingredient_management' }),
          },
          {
            enabled: permissions.farm.list_diet,
            key: 'nutrition-register-diet',
            link: `/farm/${farmSlug}/modules/precision-nutrition/diet`,
            label: Translate({ messageKey: 'diet_setup' }),
          },
        ],
      },
      //Controle Leiteiro
      {
        key: 'milk',
        icon: <Icon name="production" size={21} />,
        label: Translate({ messageKey: 'milk_control' }),
        collapsable: true,
        collapseKey: 'milk',
        items: [
          {
            enabled: permissions.farm.list_milk_production,
            key: 'milk-control',
            link: `/farm/${farmSlug}/cow-analytics/milk-control/general`,
            label: Translate({ messageKey: 'production_analysis' }),
          },
          {
            enabled: permissions.farm.list_animal && permissions.farm.list_milk_production,
            key: 'milk-control-production',
            link: `/farm/${farmSlug}/modules/milk-control/production`,
            label: Translate({ messageKey: 'production_register' }),
          },
          {
            enabled: permissions.farm.list_health_event,
            key: 'milk-withdrawal',
            link: `/farm/${farmSlug}/modules/health/withdrawal`,
            label: Translate({ messageKey: 'animals_in_withdrawal' }),
          },
          {
            enabled: permissions.farm.list_milk_quality,
            key: 'milk-control-quality',
            link: `/farm/${farmSlug}/modules/milk-control/quality`,
            label: Translate({ messageKey: 'tank_analysis' }),
          },
          {
            enabled: permissions.farm.list_milk_quality,
            key: 'milk-control-collected',
            link: `/farm/${farmSlug}/modules/milk-control/collected`,
            label: Translate({ messageKey: 'collected_milk' }),
          },
          {
            enabled:
              permissions.farm.view_service_analytics || permissions.farm.list_reproduction_event,
            key: 'milk-control-induced',
            link: permissions.farm.view_service_analytics
              ? `/farm/${farmSlug}/modules/milk-control/induced/marked`
              : `/farm/${farmSlug}/modules/milk-control/induced/realized`,
            label: Translate({ messageKey: 'lactation_induction' }),
          },
        ],
      },
      //Estresse Termico
      {
        key: 'stress',
        icon: <Icon size="large" name="thermometer-lines" />,
        label: Translate({ messageKey: 'thermal_stress' }),
        collapsable: true,
        collapseKey: 'stress',
        items: [
          {
            enabled: permissions.farm.view_temperature_history,
            key: 'thi',
            link: `/farm/${farmSlug}/modules/thermal-stress`,
            label: Translate({ messageKey: 'overview' }),
          },
        ],
      },
      //Evento de Manejo
      {
        key: 'handling',
        icon: <Icon name="handling" size={21} />,
        label: Translate({ messageKey: 'handling_events' }),
        collapsable: true,
        collapseKey: 'handling',
        items: [
          {
            enabled: permissions.farm.list_handling_event,
            key: 'handling-list',
            link: `/farm/${farmSlug}/modules/cattle-handling`,
            label: Translate({ messageKey: 'handling_register' }),
          },
        ],
      },
      //Monitoramento
      {
        key: 'monitoring',
        icon: <Icon size="large" name="wifi" />,
        label: Translate({ messageKey: 'monitoring' }),
        collapsable: true,
        collapseKey: 'monitoring',
        items: [
          {
            enabled: permissions.farm.list_device_point,
            key: 'overview',
            link: `/farm/${farmSlug}/modules/monitoring/overview`,
            label: Translate({ messageKey: 'overview' }),
          },
          {
            enabled: permissions.farm.list_animal,
            key: 'animals-list',
            link: `/farm/${farmSlug}/modules/monitoring/animals`,
            label: Translate({
              messageKey: 'item_monitored_plural',
              params: {
                item: Translate({ messageKey: 'animal_plural', lower: true }),
              },
            }),
          },
          {
            enabled: permissions.farm.list_batch && permissions.farm.list_milk_production,
            key: 'batchs-list',
            link: `/farm/${farmSlug}/modules/precision-nutrition/monitored`,
            label: Translate({
              messageKey: 'item_monitored_plural',
              params: {
                item: Translate({ messageKey: 'batch_plural', lower: true }),
              },
            }),
          },
          {
            enabled:
              permissions.farm.list_collar ||
              permissions.farm.view_expired_cow_baby ||
              permissions.farm.list_collar_review_on_farm,
            key: 'collars-list',
            link: `/farm/${farmSlug}/modules/monitoring/c-tech`,
            exact: true,
            label: Translate({ messageKey: 'collar_managment_plural' }),
          },
          {
            enabled: permissions.farm.list_cenv && permissions.farm.list_device_point,
            key: 'cenv-list',
            link: permissions.farm.list_cenv
              ? `/farm/${farmSlug}/modules/monitoring/c-env`
              : `/farm/${farmSlug}/modules/monitoring/c-env/points`,
            label: Translate({ messageKey: 'cow_comfort_managment' }),
          },
          {
            enabled: permissions.farm.list_ccom_at_farm,
            key: 'ccom-list',
            link: `/farm/${farmSlug}/modules/monitoring/c-com`,
            label: Translate({ messageKey: 'c_com_managment' }),
          },
          {
            enabled: permissions.farm.list_tokenizable_animals,
            key: 'tokenized-animals',
            link: `/farm/${farmSlug}/modules/monitoring/tokenized`,
            label: Translate({ messageKey: 'tokenized_animals' }),
          },
        ],
      },
      //CowAnalytics
      {
        enabled:
          permissions.farm.view_wellness_analytics ||
          permissions.farm.view_herd_analytics ||
          permissions.farm.view_conception_analytics ||
          (permissions.farm.view_calf_analytics && permissions.farm.list_animal_weight) ||
          permissions.farm.view_calf_analytics ||
          permissions.farm.view_heat_return_analytics ||
          (permissions.farm.list_animal && permissions.farm.list_reproduction_event) ||
          permissions.farm.view_health_analytics ||
          permissions.farm.list_reproduction_event ||
          permissions.farm.view_transition_analytics ||
          permissions.farm.view_discard_analytics ||
          permissions.farm.view_service_analytics,
        key: 'cow-analytics',
        link: `/farm/${farmSlug}/cow-analytics`,
        icon: <Icon name="chart-spline" size={20} />,
        label: Translate({ messageKey: 'cow_analytics' }),
      },
      //Lists
      {
        enabled: permissions.farm.list_farm_lists,
        timestamp: Moment('2026-07-07').endOf('day').add(1, 'month').toString(),
        key: 'lists',
        link: `/farm/${farmSlug}/lists`,
        icon: <Icon name="file-text" size={20} />,
        label: Translate({ messageKey: 'lists' }),
      },

      //Import
      {
        enabled: permissions.farm.list_farm_job,
        key: 'import',
        link: `/farm/${farmSlug}/import`,
        icon: <Icon name="import" size={21} />,
        label: Translate({ messageKey: 'import_data' }),
      },

      //Export
      {
        enabled: permissions.farm.list_farm_job,
        key: 'export',
        link: `/farm/${farmSlug}/export`,
        icon: <Icon name="export" size={21} />,
        label: Translate({ messageKey: 'export_data' }),
      },

      //Vic
      {
        enabled: permissions.farm.use_vic_gpt_cowmed || permissions.farm.use_vic_gpt,
        key: 'vic',
        link: `/farm/${farmSlug}/vic`,
        icon: <Icon size="large" name="bot-message-square" />,
        label: Translate({ messageKey: 'VIC' }),
      },
    ],
  }

  // Exemplo de uso no componente:
  const MyListItem = forwardRef((props, ref) => {
    const { children, selected, ...rest } = props
    return (
      <LinkListItem ref={ref} component={Link} selected={selected} {...rest}>
        {children}
      </LinkListItem>
    )
  })

  MyListItem.displayName = 'MyListItem'

  const renderMenuItems = options => {
    // render each item list
    const renderListItem = props => {
      const { key, link, icon, label, collapsable, collapseKey, items, timestamp } = props

      const handleOpen = () => {
        setOpen(prevState => (prevState === collapseKey ? null : collapseKey))
      }

      if (collapsable) {
        return (
          <React.Fragment>
            <ListItemButton key={key} onClick={handleOpen}>
              <StyledListItemIcon>{icon}</StyledListItemIcon>
              <StyledListItemText primary={label} />
              {open === collapseKey ? (
                <Icon size="large" name="mdi-chevron-up" />
              ) : (
                <Icon size="large" name="mdi-chevron-down" />
              )}
            </ListItemButton>
            <Collapse in={open === collapseKey} timeout="auto">
              <List component="div">
                {items.map(item => {
                  const { key, link, label, enabled } = item
                  return (
                    enabled && (
                      <MyListItem
                        key={key}
                        to={link}
                        selected={checkSelected(item)}
                        sx={{
                          paddingLeft: 7,
                          paddingTop: '5px',
                          paddingBottom: '5px',
                        }}
                      >
                        <ListItemText primary={label} />
                      </MyListItem>
                    )
                  )
                })}
              </List>
            </Collapse>
          </React.Fragment>
        )
      }
      return (
        <MyListItem key={key} to={link} selected={checkSelected(props)}>
          <StyledListItemIcon>{icon}</StyledListItemIcon>
          <StyledListItemText primary={label} />
          <NewBadge timestamp={timestamp} customStyle={{ ml: 'auto', marginTop: 0 }} />
        </MyListItem>
      )
    }

    return options
      .filter(item => {
        if (item.enabled) {
          return true
        }
        if (item.items) {
          return item.items.some(subItem => subItem.enabled)
        }
        return false
      })
      ?.map((each, i) => {
        const {
          key,
          subheader,
          link,
          icon,
          label,
          collapsable,
          collapseKey,
          items,
          exact,
          timestamp,
        } = each

        return (
          <StyledList
            key={`${key}-${i}`}
            subheader={
              subheader ? (
                <StyledSubheader
                  component="div"
                  id="nested-list-subheader-farm"
                  disableSticky
                  sx={{ marginTop: 1.5 }}
                >
                  <Typography variant="body1" gutterBottom component="span">
                    {subheader}
                  </Typography>
                </StyledSubheader>
              ) : undefined
            }
          >
            {renderListItem({
              key,
              link,
              icon,
              label,
              collapsable,
              collapseKey,
              items,
              exact,
              timestamp,
            })}
          </StyledList>
        )
      })
  }

  useMemo(() => {
    const b2bCollapseKey = modules.b2b?.find(i => i.collapsable && checkSelected(i))?.collapseKey

    const b2cCollapseKey = modules.b2c?.find(i => i.collapsable && checkSelected(i))?.collapseKey

    setOpen(b2bCollapseKey ?? b2cCollapseKey)
  }, [])

  /* SYSTEM MODULES */
  const OutFarm = () => (
    <List key="system-menu" sx={{ p: 0 }}>
      {renderMenuItems(modules.b2b)}
    </List>
  )

  /* FARM MODULES */
  const InFarm = () => (
    <List key="in-farm-menu" sx={{ p: 0 }}>
      {renderMenuItems(modules.b2c)}
    </List>
  )

  return (
    <React.Fragment>
      {!farmSlug || isBusinessEnvironment() ? <OutFarm /> : <InFarm />}
    </React.Fragment>
  )
}

export default Menu
